/**
 * File: web/src/lib/api.ts
 * =======================
 *
 * API helper (fetch wrapper with auth + error normalization)
 *
 * Purpose:
 * - Provide a single, consistent way to call the backend API (API Gateway).
 * - Normalize error handling so UI code doesn't duplicate:
 *   - JSON parsing
 *   - error extraction
 *   - 401 session-expired behavior (clear token + redirect to login)
 *   - 403 forbidden behavior (surface an "unauthorized" UI message)
 *
 * When used:
 * - Routes and UI services call apiGet/apiPost/apiDelete rather than raw fetch().
 *
 * Learner note:
 * - Lambdas typically return error JSON like { error: "..." }. This wrapper extracts that,
 *   but still handles cases where error bodies are plain text (gateway errors, misconfig).
 *
 * Gotcha:
 * - Browser CORS failures often appear as "Failed to fetch" (no HTTP status).
 *   friendlyNetworkError() translates that into a more actionable message.
 */

import type { AppConfig, ApprovedEmailItem } from "./types";
import { AuthError, ForbiddenError, friendlyNetworkError } from "./errors";
import { clearToken, login } from "./auth";

/**
 * Extract an error message from a response body string.
 *
 * Why:
 * - Our backend usually returns JSON: { error: "..." }.
 * - Some failure cases return plain text or HTML (proxy/gateway errors).
 * - We don't want JSON.parse failures to hide the original error string.
 */
function extractError(text: string): string {
  try {
    const j = JSON.parse(text);
    return j.error || j.message || text;
  } catch {
    return text;
  }
}

/**
 * Core request wrapper:
 * - performs fetch
 * - reads text once (so we can parse or display it)
 * - converts HTTP statuses into typed errors
 * - returns parsed JSON for successful responses
 *
 * Why read .text() instead of .json() directly:
 * - If the backend returns non-JSON on error, .json() would throw and we’d lose detail.
 * - Reading text lets us parse JSON when possible and still show helpful output when not.
 */
async function request<T>(
  config: AppConfig,
  path: string,
  init: RequestInit,
): Promise<T> {
  try {
    const res = await fetch(`${config.apiBaseUrl}${path}`, init);

    // Read response body as text once.
    // We'll parse JSON for successful responses and also use it for errors.
    const text = await res.text();

    // Anything outside 2xx is considered an error in our app contract.
    if (!res.ok) {
      const msg = extractError(text) || `HTTP ${res.status}`;

      /**
       * 401 Unauthorized:
       * - In this app, it usually means the token is expired or invalid.
       * - We treat that as "session expired": clear token and force re-login.
       *
       * UX detail:
       * - We pass the current path so after successful login we can navigate back.
       */
      if (res.status === 401) {
        clearToken();

        // Redirect to Cognito Hosted UI and return the user to the current page afterwards.
        login(config, window.location.pathname);

        // Throw to stop the current execution path; redirect is already happening.
        throw new AuthError("Session expired");
      }

      /**
       * 403 Forbidden:
       * - Token is valid, but user is not allowed.
       * - Common causes in this project:
       *   - user is not allowlisted
       *   - user is not in "Admins" group (for admin endpoints)
       */
      if (res.status === 403) throw new ForbiddenError(msg);

      // Everything else becomes a generic Error with the best message we can extract.
      throw new Error(msg);
    }

    // Successful responses are expected to be JSON.
    return JSON.parse(text);
  } catch (e) {
    // Preserve semantic errors so UI can handle them specifically.
    if (e instanceof AuthError || e instanceof ForbiddenError) throw e;

    // Convert low-level network/cors errors into something actionable.
    throw new Error(friendlyNetworkError(e));
  }
}

/**
 * GET wrapper:
 * - Adds Authorization header if token exists
 * - Delegates parsing and error handling to request<T>()
 */
export async function apiGet<T>(
  config: AppConfig,
  path: string,
  token?: string | null,
): Promise<T> {
  return request<T>(config, path, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

/**
 * POST wrapper:
 * - Sends JSON body
 * - Adds Authorization header if token exists
 */
export async function apiPost<T>(
  config: AppConfig,
  path: string,
  body: unknown,
  token?: string | null,
): Promise<T> {
  return request<T>(config, path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

/**
 * DELETE wrapper:
 * - Adds Authorization header if token exists
 */
export async function apiDelete<T>(
  config: AppConfig,
  path: string,
  token?: string | null,
): Promise<T> {
  return request<T>(config, path, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

export type ApprovedEmailsResponse = { items: ApprovedEmailItem[] };
